import React from 'react';
import { Modal, Form, Input, message } from 'antd';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { CHANGE_OWN_PASSWORD, ChangeOwnPasswordResult } from 'graphql/mutations/account/changeOwnPassword';
import { getUserInfo, saveUserInfo } from 'utils/auth';
import { passwordPolicyRule } from 'utils/passwordValidator';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// 与后端 MessageKey 对应
const OLD_INCORRECT_KEY = 'errors.business.account.password.incorrect';
const NOT_MEET_KEY = 'errors.business.account.password.notMeetRequirement';

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [changeOwnPassword, { loading }] = useMutation<ChangeOwnPasswordResult>(CHANGE_OWN_PASSWORD);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      // 前端校验未通过，错误已在对应输入框下高亮
      return;
    }

    try {
      const { data } = await changeOwnPassword({
        variables: { input: { oldPassword: values.oldPassword, newPassword: values.newPassword } },
      });
      const resp = data?.changeOwnPassword;

      if (resp?.success) {
        // 用 12h 宽限 token 替换本地 token，本端无需退登
        const userInfo = getUserInfo();
        if (userInfo && resp.token) {
          saveUserInfo({
            ...userInfo,
            token: resp.token,
            refreshToken: resp.refreshToken ?? undefined,
          });
        }
        message.success(t('accountSecurity.changePassword.success') as string, 2);
        handleClose();
        return;
      }

      // 失败：按后端 messageKey 定位到对应输入框下高亮
      const key = resp?.message;
      if (key === OLD_INCORRECT_KEY) {
        form.setFields([{ name: 'oldPassword', errors: [t('accountSecurity.changePassword.oldIncorrect') as string] }]);
      } else if (key === NOT_MEET_KEY) {
        form.setFields([{ name: 'newPassword', errors: [t('accountSecurity.passwordNotMeet') as string] }]);
      } else {
        message.error(key ? (t(key) as string) : (t('accountSecurity.changePassword.failed') as string));
      }
    } catch (e) {
      // 网络/GraphQL 异常由全局 errorLink 兜底处理
      message.error(t('accountSecurity.changePassword.failed') as string);
    }
  };

  return (
    <Modal
      title={t('accountSecurity.changePassword.title') as string}
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      okText={t('accountSecurity.changePassword.submit') as string}
      confirmLoading={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label={t('accountSecurity.changePassword.oldLabel') as string}
          name="oldPassword"
          rules={[{ required: true, message: t('accountSecurity.changePassword.oldPlaceholder') as string }]}
        >
          <Input.Password placeholder={t('accountSecurity.changePassword.oldPlaceholder') as string} autoComplete="current-password" />
        </Form.Item>
        <Form.Item
          label={t('accountSecurity.changePassword.newLabel') as string}
          name="newPassword"
          validateTrigger="onBlur"
          extra={t('accountSecurity.passwordHint') as string}
          rules={[
            { required: true, message: t('accountSecurity.changePassword.newPlaceholder') as string },
            passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string),
          ]}
        >
          <Input.Password placeholder={t('accountSecurity.changePassword.newPlaceholder') as string} autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          label={t('accountSecurity.changePassword.confirmLabel') as string}
          name="confirmPassword"
          dependencies={['newPassword']}
          validateTrigger="onBlur"
          rules={[
            { required: true, message: t('accountSecurity.changePassword.confirmPlaceholder') as string },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('accountSecurity.changePassword.mismatch') as string));
              },
            }),
          ]}
        >
          <Input.Password placeholder={t('accountSecurity.changePassword.confirmPlaceholder') as string} autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
